import { useEffect, type ReactNode } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { UserGoogleConnectionStatus } from '@digemp/shared';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { confirmDialog } from '@/lib/confirm-dialog';
import {
  useConnectMyGoogleCalendar,
  useConnectMyGoogleCalendarInPopup,
  useDisconnectMyGoogleCalendar,
  useMyGoogleCalendar,
  openCentredPopup,
  GOOGLE_RETURN_CHANNEL,
} from '@/hooks/queries/useResources';
import { useGoogleSignInStatus } from '@/hooks/queries/useAuth';

/**
 * A person's own Google Calendar connection (docs/google-hybrid-calendar-plan.md):
 * meetings they book are then sent from their own calendar, so they are the
 * organiser. Only shown when the org uses one Google account.
 */

function useMine(): UserGoogleConnectionStatus | undefined {
  const { data } = useMyGoogleCalendar();
  return (data as { data?: UserGoogleConnectionStatus } | undefined)?.data;
}

/** Connect my Google Calendar in a popup, so the page (and a half-filled form) stays put. */
function useConnectInPopup(): { start: () => void; isPending: boolean } {
  const connect = useConnectMyGoogleCalendarInPopup();
  const start = () => {
    const popup = openCentredPopup('digemp-google-connect');
    if (!popup) {
      toast.error('Your browser blocked the Google window. Allow pop-ups for DigEmp and try again.');
      return;
    }
    // The answer is announced by useGoogleReturnNotice (AppShell), which hears the popup's message too.
    connect.mutate(popup);
  };
  return { start, isPending: connect.isPending };
}

/**
 * For a booking form. `active` = a new booking in a room that has a Google
 * calendar (only those need the connection). `blocked` is true when the org
 * requires it and this person has no working connection — the server refuses
 * the booking (GOOGLE_CONNECTION_REQUIRED) until they connect.
 */
export function useGoogleConnectGate(active: boolean): { blocked: boolean; banner: ReactNode } {
  const mine = useMine();
  const connect = useConnectInPopup();
  const needed = !!mine?.available && mine.bookerConnection !== 'off' && (!mine.connected || mine.broken);
  if (!active || !mine || !needed) return { blocked: false, banner: null };
  const required = mine.bookerConnection === 'required';
  const message = mine.broken
    ? 'Google no longer accepts DigEmp’s access to your calendar. Reconnect it so the meetings you book come from you.'
    : required
      ? 'Connect your Google account to book. Meetings you book are sent from your own calendar, so you are the organiser and replies come to you.'
      : 'Connect your Google account so the meetings you book come from you. Without it, the invitation is sent from the room’s calendar.';
  return {
    blocked: required,
    banner: (
      <div
        data-testid="google-connect-banner"
        className={`rounded-lg border px-3 py-2.5 text-sm flex flex-col sm:flex-row sm:items-center gap-3 ${
          required || mine.broken ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-primary-200 bg-primary-50 text-primary-900'
        }`}
      >
        <div className="flex items-start gap-2 flex-1">
          <FontAwesomeIcon icon={mine.broken ? faTriangleExclamation : faCalendarCheck} className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{message}</span>
        </div>
        {/* type="button": the banner sits inside the interview form — it must not submit it. */}
        <Button type="button" size="sm" onClick={connect.start} loading={connect.isPending} className="shrink-0">
          {mine.broken ? 'Reconnect Google account' : 'Connect Google account'}
        </Button>
      </div>
    ),
  };
}

/**
 * True when the org requires a connected Google account and this person has no
 * working one — a room booking with a Google calendar would be refused.
 */
export function useGoogleConnectionMissing(): boolean {
  const mine = useMine();
  return !!mine?.available && mine.bookerConnection === 'required' && (!mine.connected || mine.broken);
}

/**
 * The first thing a scheduling form shows while `useGoogleConnectionMissing()`
 * holds — before any slot is picked, so nobody fills a form they cannot save.
 * `onSkip` offers a way on that needs no Google (e.g. an online interview).
 */
export function GoogleConnectFirst({ onSkip, skipLabel }: { onSkip?: () => void; skipLabel?: string }) {
  const mine = useMine();
  const connect = useConnectInPopup();
  return (
    <div data-testid="google-connect-first" className="mx-auto flex max-w-md flex-col items-center gap-4 py-10 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600">
        <FontAwesomeIcon icon={mine?.broken ? faTriangleExclamation : faCalendarCheck} className="h-5 w-5" />
      </span>
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-stone-900">{mine?.broken ? 'Reconnect your Google account' : 'Connect your Google account first'}</h3>
        <p className="text-sm text-stone-600">
          {mine?.broken
            ? 'Google no longer accepts DigEmp’s access to your calendar.'
            : 'Your organisation requires a connected Google account to book rooms.'}{' '}
          Meetings you book are sent from your own calendar, so you are the organiser and replies come to you.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        {/* type="button": this sits inside a form — it must not submit it. */}
        <Button type="button" onClick={connect.start} loading={connect.isPending}>
          {mine?.broken ? 'Reconnect Google account' : 'Connect Google account'}
        </Button>
        {onSkip && (
          <Button type="button" variant="secondary" onClick={onSkip}>
            {skipLabel ?? 'Continue without'}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Wraps the Bookings pages. When the org requires a connected calendar and this
 * person has no working one, the first thing they see on any Bookings page is a
 * prompt to connect — it stays until they do. Closing it leaves Bookings.
 * People whose own org uses no Google account never see it (shared rooms only).
 */
export function GoogleConnectRequiredOutlet() {
  const mine = useMine();
  const connect = useConnectInPopup();
  const navigate = useNavigate();
  const open =
    !!mine?.available && mine.bookerConnection === 'required' && (!mine.connected || mine.broken);
  const leave = () => navigate('/dashboard');
  return (
    <>
      <Outlet />
      <Modal
        open={open}
        onClose={leave}
        size="sm"
        title={mine?.broken ? 'Reconnect your Google account' : 'Connect your Google account'}
        footer={
          <>
            <Button variant="secondary" onClick={leave}>
              Not now
            </Button>
            <Button onClick={connect.start} loading={connect.isPending}>
              {mine?.broken ? 'Reconnect Google account' : 'Connect Google account'}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3 text-sm text-gray-700">
          <FontAwesomeIcon
            icon={mine?.broken ? faTriangleExclamation : faCalendarCheck}
            className="w-5 h-5 mt-0.5 shrink-0 text-primary-600"
          />
          <div className="space-y-2">
            <p>
              {mine?.broken
                ? 'Google no longer accepts DigEmp’s access to your calendar.'
                : 'Your organisation requires a connected Google account to use room bookings.'}
            </p>
            <p>
              Meetings you book are sent from your own calendar, so you are the organiser and replies come to you.
              DigEmp also reads when you are busy, so colleagues booking with you see it.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * Profile card: my own Google account — the one I sign in with (when Google
 * sign-in is on) and whose calendar sends the meetings I book.
 */
export function GoogleCalendarCard() {
  const mine = useMine();
  const connect = useConnectMyGoogleCalendar();
  const disconnect = useDisconnectMyGoogleCalendar();
  const { data: signInWithGoogle } = useGoogleSignInStatus();
  if (!mine?.available) return null;
  // With the setting `off` new meetings come from the room calendar, so there is nothing to connect for —
  // an existing login still shows, so its owner can disconnect it.
  if (mine.bookerConnection === 'off' && !mine.connected && !mine.broken) return null;

  const onDisconnect = async () => {
    const ok = await confirmDialog({
      title: 'Disconnect Google account?',
      message:
        'Meetings you book from now on are sent from the room’s calendar. Meetings you already organise stay in your calendar, and DigEmp keeps updating them until they are over.' +
        (mine.bookerConnection === 'required' ? ' Your organisation requires a connected calendar to book, so you will not be able to book until you reconnect.' : '') +
        (signInWithGoogle ? ' You can still sign in with Google.' : ''),
      confirmLabel: 'Disconnect',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await disconnect.mutateAsync();
      toast.success('Google account disconnected');
    } catch {
      /* the api client already showed the reason */
    }
  };

  const state = mine.broken ? 'broken' : mine.connected ? 'connected' : 'none';
  return (
    <div data-testid="google-calendar-card" className="bg-white rounded-lg border border-stone-100 shadow-sm p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Google account</h3>
          <p className="text-sm text-gray-500 mt-1">
            {signInWithGoogle ? 'One Google account for DigEmp: you sign in with it, and the meetings ' : 'The meetings '}
            you book are sent from its calendar, so you are the organiser and replies come to you. DigEmp also reads when
            you are busy, so colleagues booking with you see it.
          </p>
        </div>
        <span
          className={`shrink-0 px-2.5 py-1 text-xs rounded-sm ${
            state === 'connected' ? 'bg-primary-100 text-primary-800' : state === 'broken' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {state === 'connected' ? 'Connected' : state === 'broken' ? 'Needs reconnecting' : 'Not connected'}
        </span>
      </div>
      {mine.email && state !== 'none' && (
        <p className="mt-3 text-sm text-gray-700">
          <span className="text-gray-500">Account:</span> {mine.email}
        </p>
      )}
      {state === 'broken' && (
        <p className="mt-2 text-sm text-amber-800">
          Google no longer accepts DigEmp’s access. Until you reconnect, new meetings come from the room’s calendar and
          changes to meetings you organise cannot reach Google.
        </p>
      )}
      {state === 'none' && mine.bookerConnection === 'required' && (
        <p className="mt-2 text-sm text-amber-800">Your organisation requires this before you can book a room.</p>
      )}
      <div className="mt-4 flex gap-2">
        {state === 'connected' ? (
          <Button variant="secondary" onClick={onDisconnect} loading={disconnect.isPending}>
            Disconnect
          </Button>
        ) : (
          <Button onClick={() => connect.mutate(undefined)} loading={connect.isPending}>
            {state === 'broken' ? 'Reconnect Google account' : 'Connect Google account'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** After Google sends the browser back (?google=connected | error&reason=…): say what happened, then tidy the URL. */
export function useGoogleReturnNotice(): void {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  // The connect popup reports here too, so its answer lands even when the form that opened it stopped waiting.
  useEffect(() => {
    const channel = new BroadcastChannel(GOOGLE_RETURN_CHANNEL);
    channel.onmessage = (e: MessageEvent<string>) => {
      const back = new URLSearchParams(e.data);
      const why = back.get('reason');
      if (back.get('google') === 'connected') toast.success('Google account connected', { id: 'google-return' });
      else toast.error(why ? `Google account not connected: ${why}` : 'Google account not connected', { id: 'google-return' });
      qc.invalidateQueries({ queryKey: ['google-calendar-me'] });
    };
    return () => channel.close();
  }, [qc]);
  const result = params.get('google');
  const reason = params.get('reason');
  useEffect(() => {
    if (!result) return;
    // One toast per return, even when the effect runs twice (React strict mode in development).
    if (result === 'connected') toast.success('Google account connected', { id: 'google-return' });
    else toast.error(reason ? `Google account not connected: ${reason}` : 'Google account not connected', { id: 'google-return' });
    const next = new URLSearchParams(params);
    next.delete('google');
    next.delete('reason');
    setParams(next, { replace: true });
  }, [result, reason, params, setParams]);
}
